/**
 * Meta Pixel for the BazeBuild app (app.bazebuild.com). Same public id as the marketing site.
 * Loaded from a module so the SPA does not need unsafe-inline in script-src.
 *
 * Events that the API also sends through the Conversions API (CompleteRegistration, Lead, Purchase)
 * carry a deterministic eventID — the server uses the same one, so Meta counts each conversion once.
 */

/** BazeBuild pixel from Events Manager. Keep in sync with apps/web/src/lib/meta-pixel.ts. */
export const DEFAULT_META_PIXEL_ID = "1001079093009632";

const PIXEL_ID = /^\d{8,20}$/;

type FbqFn = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: FbqFn;
  loaded: boolean;
  version: string;
};

/**
 * Id to load, or "" when the pixel is off.
 * Unset or empty uses `fallback` (the BazeBuild pixel in a production build, "" in local dev).
 * `off` disables it. Any other digits replace the id. A non-numeric value stays off.
 */
export function resolveMetaPixelId(raw: string | undefined, fallback: string): string {
  const id = raw?.trim() ?? "";
  if (id === "off") return "";
  if (id) return PIXEL_ID.test(id) ? id : "";
  return fallback;
}

/** Id for this build. Production with no env var uses the BazeBuild pixel. */
export function metaPixelId(): string {
  return resolveMetaPixelId(import.meta.env.VITE_META_PIXEL_ID, import.meta.env.PROD ? DEFAULT_META_PIXEL_ID : "");
}

/** Load fbevents.js once, init, and track the first PageView. Later route changes call fbq directly. */
export function installMetaPixel(id: string): void {
  if (!PIXEL_ID.test(id) || window.fbq) return;
  const n = function (...args: unknown[]) {
    // fbevents.js calls through this stub with `this` set to the fbq function, matching Meta's snippet.
    // eslint-disable-next-line prefer-spread
    if (n.callMethod) n.callMethod.apply(n, args);
    else n.queue.push(args);
  } as FbqFn;
  window.fbq = n;
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  const t = document.createElement("script");
  t.async = true;
  t.src = "https://connect.facebook.net/en_US/fbevents.js";
  const s = document.getElementsByTagName("script")[0];
  if (s?.parentNode) s.parentNode.insertBefore(t, s);
  else document.head.appendChild(t);
  n("init", id);
  n("track", "PageView");
}

/** Shared with the API's Conversions API events (apps/api/internal/metacapi). */
export const eventIds = {
  registration: (userId: string) => `reg-${userId}`,
  lead: (siteId: string) => `site-${siteId}`,
  purchase: (orderId: string) => `order-${orderId}`,
  publish: (siteId: string) => `publish-${siteId}`,
  /** InitiateCheckout fires before an order exists: a fresh id per checkout visit, sent with the checkout request. */
  checkout: () => `ic-${crypto.randomUUID()}`,
};

let pendingUser: { id: string; email: string } | null = null;
let identified = "";

/**
 * Advanced matching for signed-in users (the pixel hashes these before sending). Auth can resolve
 * before the pixel is installed, so the user is kept and applied by flushIdentity() after install.
 */
export function identify(user: { id: string; email: string } | null) {
  pendingUser = user;
  flushIdentity();
}

export function flushIdentity() {
  const id = metaPixelId();
  if (!id || !window.fbq || !pendingUser || identified === pendingUser.id) return;
  identified = pendingUser.id;
  window.fbq("init", id, { external_id: pendingUser.id, em: pendingUser.email.trim().toLowerCase() });
}

/** No-op until the pixel is installed (off in dev unless VITE_META_PIXEL_ID is set). */
export function track(event: string, params?: Record<string, unknown>, eventId?: string) {
  window.fbq?.("track", event, params ?? {}, eventId ? { eventID: eventId } : undefined);
}

export function trackCustom(event: string, params?: Record<string, unknown>, eventId?: string) {
  window.fbq?.("trackCustom", event, params ?? {}, eventId ? { eventID: eventId } : undefined);
}
